import React, { useEffect, useMemo, useRef, useState } from "react";
import { VIZ, fmtNum } from "./charts";
import { cx } from "../../pages/admin/_shared";

// ─── DonutChart: tỷ trọng các phần (tối đa ~5 phần), hover xem chi tiết ────────
export function DonutChart({ parts, size = 168, thickness = 20, centerLabel = "Tổng", colors = VIZ, empty = "Chưa có dữ liệu" }) {
  const [hover, setHover] = useState(null);
  const total = parts.reduce((a, p) => a + p.value, 0);
  const r = (size - thickness) / 2;
  const c = size / 2;
  const circumference = 2 * Math.PI * r;
  const GAP = parts.filter((p) => p.value > 0).length > 1 ? 2.5 : 0; // khoảng hở 2px giữa các phần

  const arcs = useMemo(() => {
    let offset = 0;
    return parts.map((p, i) => {
      const len = total > 0 ? (p.value / total) * circumference : 0;
      const arc = { ...p, color: p.color || colors[i % colors.length], dash: Math.max(0, len - GAP), offset, len };
      offset += len;
      return arc;
    });
  }, [parts, total, circumference, colors, GAP]);

  const active = hover != null ? arcs[hover] : null;

  if (total === 0) return <p className="py-10 text-center text-sm text-admin-textMuted">{empty}</p>;

  return (
    <div className="flex flex-col items-center gap-5">
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} role="img" aria-label={parts.map((p) => `${p.label} ${p.value}`).join(", ")} onPointerLeave={() => setHover(null)}>
          <circle cx={c} cy={c} r={r} fill="none" stroke="rgb(var(--admin-muted))" strokeWidth={thickness} />
          {arcs.map((a, i) =>
            a.dash > 0 ? (
              <circle
                key={a.label}
                cx={c}
                cy={c}
                r={r}
                fill="none"
                stroke={a.color}
                strokeWidth={hover === i ? thickness + 3 : thickness}
                strokeDasharray={`${a.dash} ${circumference - a.dash}`}
                strokeDashoffset={-a.offset}
                transform={`rotate(-90 ${c} ${c})`}
                className="cursor-default transition-[stroke-width] duration-150"
                onPointerEnter={() => setHover(i)}
              />
            ) : null
          )}
        </svg>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
          <span className="text-[11px] text-admin-textMuted">{active ? active.label : centerLabel}</span>
          <span className="text-2xl font-semibold leading-tight tracking-tight text-admin-text">
            {active ? `${Math.round((active.value / total) * 100)}%` : fmtNum(total)}
          </span>
          {active ? <span className="text-[11px] text-admin-textMuted">{fmtNum(active.value)}</span> : null}
        </div>
      </div>

      <ul className="w-full min-w-0 flex-1 space-y-2">
        {arcs.map((a, i) => (
          <li
            key={a.label}
            onPointerEnter={() => setHover(i)}
            onPointerLeave={() => setHover(null)}
            className={cx("flex items-center justify-between gap-3 rounded-md px-2 py-1 text-sm transition", hover === i && "bg-admin-muted")}
          >
            <span className="flex min-w-0 items-center gap-2 text-admin-text">
              <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: a.color }} />
              <span className="truncate">{a.label}</span>
            </span>
            <span className="shrink-0 text-xs text-admin-textMuted">
              <span className="font-semibold tabular-nums text-admin-text">{fmtNum(a.value)}</span> · {Math.round((a.value / total) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── ProgressRing: điểm 0–100 ────────────────────────────────────────────────
export function ProgressRing({ value, size = 112, thickness = 10, label }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const r = (size - thickness) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;
  // Màu theo mức: tốt / cần cải thiện / thấp (kèm chữ để không phụ thuộc màu)
  const color = v >= 80 ? "#16a34a" : v >= 50 ? "#d97706" : "#dc2626";
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} role="img" aria-label={`${label || "Điểm"}: ${v} trên 100`}>
      <svg width={size} height={size}>
        <circle cx={c} cy={c} r={r} fill="none" stroke="rgb(var(--admin-muted))" strokeWidth={thickness} />
        <circle
          cx={c}
          cy={c}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={`${(v / 100) * circ} ${circ}`}
          transform={`rotate(-90 ${c} ${c})`}
          className="transition-[stroke-dasharray] duration-700"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[26px] font-semibold leading-none tracking-tight text-admin-text">{v}</span>
        <span className="mt-0.5 text-[10px] text-admin-textMuted">/ 100</span>
      </div>
    </div>
  );
}


// ─── CalendarHeatmap: mỗi ô là một ngày (cột = tuần, hàng = thứ) — kiểu biểu đồ đóng góp của GitHub ─────
const CAL_DAYS = ["T2", "", "T4", "", "T6", "", "CN"];
const MONTHS_SHORT = ["Th1", "Th2", "Th3", "Th4", "Th5", "Th6", "Th7", "Th8", "Th9", "Th10", "Th11", "Th12"];
const utcDate = (key) => new Date(`${key}T00:00:00Z`);
const keyOf = (d) => d.toISOString().slice(0, 10);

/**
 * @param days [{ date: "YYYY-MM-DD", value: number|null }] — null = ngày chưa đo chỉ số này
 */
export function CalendarHeatmap({ days, unit = "phiên", empty = "Chưa có dữ liệu" }) {
  const [hover, setHover] = useState(null);

  const model = useMemo(() => {
    if (!days?.length) return null;
    const byKey = new Map(days.map((d) => [d.date, d.value]));
    const dimKey = new Set(days.filter((d) => d.dim).map((d) => d.date));
    const first = utcDate(days[0].date);
    const last = utcDate(days[days.length - 1].date);
    // Lùi về thứ Hai của tuần đầu tiên
    const start = new Date(first);
    start.setUTCDate(start.getUTCDate() - ((start.getUTCDay() + 6) % 7));
    const weeks = [];
    for (let cur = new Date(start); cur <= last; ) {
      const col = [];
      for (let r = 0; r < 7; r += 1) {
        const k = keyOf(cur);
        const inRange = cur >= first && cur <= last;
        col.push({ key: k, inRange, dim: dimKey.has(k), value: inRange ? (byKey.has(k) ? byKey.get(k) : 0) : undefined, month: cur.getUTCMonth(), day: cur.getUTCDate() });
        cur.setUTCDate(cur.getUTCDate() + 1);
      }
      weeks.push(col);
    }
    // Ngưỡng màu theo phân vị các ngày có số (giống GitHub) để vài ngày đột biến không làm mọi ô nhạt hết
    const vals = days.map((d) => d.value).filter((v) => v > 0).sort((a, b) => a - b);
    const q = (p) => (vals.length ? vals[Math.min(vals.length - 1, Math.floor(p * vals.length))] : 0);
    const thresholds = [q(0.25), q(0.5), q(0.75)];
    const measured = days.filter((d) => d.value != null);
    const total = measured.reduce((a, d) => a + d.value, 0);
    const best = measured.reduce((b, d) => (d.value > (b?.value ?? -1) ? d : b), null);
    const zero = measured.filter((d) => d.value === 0).length;
    return { weeks, thresholds, total, best, zero, measured: measured.length };
  }, [days]);

  // Cuộn tới ngày mới nhất (bên phải) khi dữ liệu đổi
  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
  }, [model]);

  if (!model || model.total === 0) return <p className="py-10 text-center text-sm text-admin-textMuted">{empty}</p>;

  const level = (v) => {
    if (v == null || v <= 0) return 0;
    const [a, b, c] = model.thresholds;
    return v <= a ? 1 : v <= b ? 2 : v <= c ? 3 : 4;
  };
  const LEVEL_BG = [
    "rgb(var(--admin-muted))",
    "color-mix(in srgb, var(--viz-1) 28%, white)",
    "color-mix(in srgb, var(--viz-1) 52%, white)",
    "color-mix(in srgb, var(--viz-1) 76%, white)",
    "var(--viz-1)",
  ];
  const cell = model.weeks.length <= 14 ? 22 : model.weeks.length <= 30 ? 16 : 13;
  const fmtDate = (k) => utcDate(k).toLocaleDateString("vi-VN", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" });

  return (
    <div>
      <div className="overflow-x-auto pb-1" ref={scrollRef}>
        <div className="inline-flex gap-2">
          {/* nhãn thứ */}
          <div className="flex flex-col gap-[3px] pt-[18px]">
            {CAL_DAYS.map((d, i) => (
              <span key={i} className="text-[10px] leading-none text-admin-textMuted" style={{ height: cell, lineHeight: `${cell}px` }}>
                {d}
              </span>
            ))}
          </div>
          <div>
            {/* nhãn tháng: đặt ở tuần chứa ngày 1 của tháng (hoặc tuần đầu tiên) */}
            <div className="flex gap-[3px]" style={{ height: 15, marginBottom: 3 }}>
              {model.weeks.map((col, i) => {
                const firstOfMonth = col.find((c) => c.inRange && c.day === 1);
                const label = firstOfMonth ? MONTHS_SHORT[firstOfMonth.month] : i === 0 ? MONTHS_SHORT[(col.find((c) => c.inRange) || col[0]).month] : "";
                return (
                  <span key={i} className="relative text-[10px] text-admin-textMuted" style={{ width: cell }}>
                    <span className="absolute left-0 top-0 whitespace-nowrap">{label}</span>
                  </span>
                );
              })}
            </div>
            <div className="flex gap-[3px]" onPointerLeave={() => setHover(null)}>
              {model.weeks.map((col, i) => (
                <div key={i} className="flex flex-col gap-[3px]">
                  {col.map((c) =>
                    c.inRange ? (
                      <button
                        type="button"
                        key={c.key}
                        aria-label={`${fmtDate(c.key)}: ${c.value == null ? "chưa đo" : `${fmtNum(c.value)} ${unit}`}`}
                        onPointerEnter={() => setHover(c)}
                        onFocus={() => setHover(c)}
                        onBlur={() => setHover(null)}
                        className={cx("rounded-[3px] outline-none transition", c.dim && "opacity-35", hover?.key === c.key && "ring-2 ring-admin-text/70 opacity-100")}
                        style={{
                          width: cell,
                          height: cell,
                          background: c.value == null ? "repeating-linear-gradient(45deg, rgb(var(--admin-muted)) 0 3px, transparent 3px 6px)" : LEVEL_BG[level(c.value)],
                        }}
                      />
                    ) : (
                      <span key={c.key} style={{ width: cell, height: cell }} />
                    )
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-admin-textMuted">
        <p className="min-h-[1rem]">
          {hover ? (
            <>
              <span className="font-semibold capitalize text-admin-text">{fmtDate(hover.key)}</span> ·{" "}
              {hover.value == null ? "chưa đo chỉ số này" : `${fmtNum(hover.value)} ${unit}`}
            </>
          ) : (
            <>
              Cao nhất: <span className="font-semibold text-admin-text">{model.best ? fmtDate(model.best.date) : "—"}</span>
              {model.best ? ` (${fmtNum(model.best.value)} ${unit})` : ""} · TB {fmtNum(model.total / Math.max(1, model.measured))} {unit}/ngày ·{" "}
              {model.zero ? `${model.zero} ngày không có ${unit}` : `ngày nào cũng có ${unit}`}
            </>
          )}
        </p>
        <div className="flex items-center gap-1.5" aria-hidden>
          <span>Ít</span>
          {LEVEL_BG.map((bg, i) => (
            <span key={i} className="h-3 w-3 rounded-[3px]" style={{ background: bg }} />
          ))}
          <span>Nhiều</span>
        </div>
      </div>
    </div>
  );
}
